import React, {useEffect, useMemo, useState} from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead, TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {Edit, Gavel, KeyboardArrowDown, RestartAlt, SwapHoriz, TrendingUp, TrendingDown} from '@mui/icons-material';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../../assets/lottie/loading.json';
import api from '../../../../api/axios';
import {useCurrency} from '../../../../hooks/useCurrency';
import {moneyFromMinor} from '../../../utils/UnitConversion.utils';
import TradeActionModal from '../modals/TradeActionModal';
import GlobalTradeCleanupModal from '../modals/GlobalTradeCleanupModal';
import {ItemDTO} from '../../../user/on-sale/types';
import {RARITY_COLORS, RarityGrade} from '../../../utils/RarityColors';
import {Trade, wearMap} from '../../../../types/trades';
import TradeStatusStepper from '../../profile/trade-history/TradeStatusStepper';

export type IdSearchType = 'tradeGroupId' | 'tradeId' | 'buyGroupId' | 'sellGroupId' | 'sellerId' | 'buyerId';

const ID_SEARCH_OPTIONS: {value: IdSearchType; label: string}[] = [
  {value: 'tradeGroupId', label: 'Trade Group ID'},
  {value: 'tradeId', label: 'Trade ID'},
  {value: 'buyGroupId', label: 'Buy Group ID'},
  {value: 'sellGroupId', label: 'Sell Group ID'},
  {value: 'sellerId', label: 'Seller ID'},
  {value: 'buyerId', label: 'Buyer ID'}
];

export type TradeStatus =
    | 'WAITING_FOR_SELLER_CONFIRM' | 'WAITING_FOR_SELLER_CONFIRM_CANCELLED'
    | 'WAITING_FOR_SELLER_SEND' | 'WAITING_FOR_SELLER_SEND_CANCELLED'
    | 'WAITING_FOR_BUYER_ACCEPT' | 'WAITING_FOR_BUYER_ACCEPT_CANCELLED'
    | 'WAITING_FOR_VERIFICATION' | 'WAITING_FOR_VERIFICATION_CANCELLED'
    | 'LOGIN_SECURE_SELLER_VERIFICATION_REQUIRED' | 'LOGIN_SECURE_BUYER_VERIFICATION_REQUIRED'
    | 'COMPLETED' | 'COMPLETED_CANCELLED' | 'SYSTEM_CANCELLED';

// Mirrors the backend SystemTradeDTO (flat trade rows enriched with group/status info)
export interface TradeSystem {
  id: string;
  offerId: string;
  buyGroupId: string;
  sellGroupId: string;
  tradeGroupId: string | null;
  tradeGroupLocked: boolean | null;
  effectiveTradeStatus: TradeStatus;
  effectiveSteamTradeOfferStatus: string | null;
  cancelType: string | null;
  statusUpdateDate: string | null;
  priceMinor: number;
  item: ItemDTO;
  sellerUserId: string;
  sellerSteamAccountId: string;
  sellerSteamAvatarUrl: string | null;
  sellerSteamProfileUrl: string | null;
  buyerUserId: string;
  buyerSteamAccountId: string;
  buyerSteamAvatarUrl: string | null;
  buyerSteamProfileUrl: string | null;
  snapshotJson: string;
  creationDate: string;
  ledgerTransactions?: any[];
}

interface InlineLedgerProps {
  groupId: string;
  money: (minor: number) => string;
}

// Renders the financial ledger breakdown lazily upon dropdown expansion
const InlineLedger = ({groupId, money}: InlineLedgerProps) => {
  const [loading, setLoading] = useState(false);
  const [ledgerGroup, setLedgerGroup] = useState<any>(null);

  useEffect(() => {
    fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const lRes = await api.get(`/system/ledger/groups?search=${groupId}`);
      if (lRes.data?.content && lRes.data.content.length > 0) {
        setLedgerGroup(lRes.data.content[0]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
        <div className="flex justify-center items-center py-6 bg-neutral-900/30">
          <Lottie animationData={loadingAnimation} loop className="w-24"/>
        </div>
    );
  }

  return (
      <div className="bg-neutral-900/60 p-3 rounded-lg border border-white/5 space-y-2">
        <Typography className="!text-xs !font-bold uppercase tracking-wider text-gray-400">Financial Ledger Breakdown</Typography>
        {ledgerGroup?.transactions ? (
            ledgerGroup.transactions.map((tx: any) => (
                <div key={tx.id} className="space-y-1">
                  <div className="flex justify-between items-center text-[10px] font-mono text-gray-400 border-b border-white/5 pb-1">
                    <span>Transaction #{tx.id} ({tx.status})</span>
                    <span>Gross: {money(tx.totalGrossAmountMinor)}</span>
                  </div>
                  {tx.entries?.map((entry: any) => {
                    const isPos = entry.amountMinor >= 0;
                    return (
                        <div key={entry.id} className="flex justify-between items-center text-xs py-1 px-2 rounded bg-neutral-800/30">
                          <div className="flex items-center gap-2 text-gray-300">
                            {isPos ? <TrendingUp className="text-emerald-500" sx={{fontSize: 14}}/> : <TrendingDown className="text-red-500" sx={{fontSize: 14}}/>}
                            <span className="font-medium uppercase text-[11px]">{entry.entryType.replace(/_/g, ' ')}</span>
                            {entry.onHold && <span className="text-[9px] bg-yellow-500/20 text-yellow-400 px-1 rounded font-bold">ON HOLD</span>}
                          </div>
                          <span className={`font-mono font-bold ${isPos ? 'text-emerald-400' : 'text-red-400'}`}>
                            {isPos ? '+' : '-'}{money(entry.amountMinor)}
                          </span>
                        </div>
                    );
                  })}
                </div>
            ))
        ) : (
            <div className="text-xs text-gray-500 italic">No ledger transactions recorded yet.</div>
        )}
      </div>
  );
};

interface InlineTimelineProps {
  tradeId: string;
}

// Renders the gradual trade timeline (trade steps + group steps + steam references) for a single trade
const InlineTimeline = ({tradeId}: InlineTimelineProps) => {
  const [loading, setLoading] = useState(false);
  const [timelineData, setTimelineData] = useState<any>(null);

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId]);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trade/${tradeId}/timeline`);
      setTimelineData(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
        <div className="flex justify-center items-center py-6 bg-neutral-900/30">
          <Lottie animationData={loadingAnimation} loop className="w-24"/>
        </div>
    );
  }

  return (
      <div className="flex flex-col gap-3 p-4 bg-neutral-950/60 border-y border-white/10 rounded-xl my-2">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
          Trade Timeline #{tradeId}
        </div>
        {timelineData?.offers && timelineData.offers.length > 0 ? (
            timelineData.offers.map((offer: any, offerIdx: number) => (
                <div key={offer.id || offerIdx} className="border border-white/10 p-3 bg-neutral-900/50 rounded-lg">
                  <div className="flex justify-between items-center mb-2 pb-1 border-b border-white/5">
                    <span className="text-xs font-bold text-blue-400 font-mono">
                      {offer.label ? offer.label : `Reference ID: ${offer.steamTradeOfferId || 'N/A'}`}
                    </span>
                    <span className={`text-[9px] px-2 py-0.5 font-black uppercase rounded ${offer.latest ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-gray-500/10 text-gray-400'}`}>
                      {offer.latest ? 'Active State' : 'Historical State'}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2 pl-2 border-l-2 border-white/10 ml-1">
                    {offer.statuses?.map((status: any, statusIdx: number) => (
                        <div key={status.id || statusIdx} className="flex flex-col gap-1 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-gray-200">Status: {status.steamTradeReferenceStatusType || 'AUDIT_STEP'}</span>
                            <span className="text-[10px] text-gray-500 font-mono">{status.createdAt ? new Date(status.createdAt).toLocaleString('de-DE') : ''}</span>
                          </div>
                          <div className="text-[11px] text-gray-400 grid grid-cols-2 gap-2">
                            <div>Report Type: <span className="font-mono text-gray-300">{status.reportType || 'N/A'}</span></div>
                            <div>Cancel Reason: <span className="font-mono text-gray-300">{status.steamTradeCancelReason || 'N/A'}</span></div>
                          </div>
                        </div>
                    ))}
                  </div>
                </div>
            ))
        ) : (
            <div className="text-center py-4 text-gray-500 text-xs uppercase font-bold">No timeline data available.</div>
        )}
      </div>
  );
};

const SystemTrades = () => {
  const {convertFromUSD, currencySymbol, formatAmountWithoutCurrency} = useCurrency();

  const [isGrouped, setIsGrouped] = useState(true);

  const [trades, setTrades] = useState<TradeSystem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [idSearchType, setIdSearchType] = useState<IdSearchType>('tradeGroupId');
  const [idSearchValue, setIdSearchValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [actionModal, setActionModal] = useState<{ open: boolean, mode: 'SINGLE' | 'BATCH', trade?: TradeSystem, entityId?: string, entityType?: string }>({open: false, mode: 'SINGLE'});
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);

  const money = (minor: number) =>
      `${currencySymbol}${formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(minor)))}`;

  // Fetch flat, enriched trades; grouping is applied client-side (mirrors the user trade view)
  const fetchData = async () => {
    setLoading(true);
    setSelectedIds([]);
    setExpandedKey(null);
    try {
      const response = await api.get('/system/trades', {
        params: {
          page,
          size: rowsPerPage,
          isGrouped,
          status: statusFilter === 'ALL' ? undefined : statusFilter,
          [idSearchType]: idSearchValue.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          search: searchValue || undefined
        }
      });
      const tradeList = response.data.content;
      setTrades(tradeList);
      setTotalCount(response.data.totalElements);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, startDate, endDate, searchValue, isGrouped, idSearchType, idSearchValue]);

  /**
   * Hybrid grouping identical in principle to the user trade view:
   *  - locked trade group  -> group by tradeGroupId
   *  - otherwise           -> pre-group by the seller<->buyer pair (a future trade group is always 1 seller / 1 buyer)
   */
  const groups = useMemo((): [string, TradeSystem[]][] => {
    if (!isGrouped) return [];
    const map = new Map<string, TradeSystem[]>();
    trades.forEach(trade => {
      const key = trade.tradeGroupLocked && trade.tradeGroupId
          ? `group_${trade.tradeGroupId}`
          : `pair_${trade.sellerSteamAccountId}_${trade.buyerSteamAccountId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(trade);
    });
    return Array.from(map.entries());
  }, [trades, isGrouped]);

  const pageTradeIds = trades.map(t => t.id);
  const isAllSelectedOnPage = pageTradeIds.length > 0 && pageTradeIds.every(id => selectedIds.includes(id));
  const isSomeSelectedOnPage = pageTradeIds.some(id => selectedIds.includes(id)) && !isAllSelectedOnPage;

  const toggleSelectAll = () => {
    if (isAllSelectedOnPage) {
      setSelectedIds(prev => prev.filter(id => !pageTradeIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageTradeIds])));
    }
  };

  const toggleSelect = (ids: string[]) => {
    setSelectedIds(prev => {
      const allSelected = ids.every(id => prev.includes(id));
      return allSelected ? prev.filter(id => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]));
    });
  };

  const toggleExpand = (key: string) => {
    setExpandedKey(prev => prev === key ? null : key);
  };

  // Status badge color, based on the effective trade status
  const getStatusStyle = (status?: string | null) => {
    const safe = status || 'UNKNOWN';
    if (safe.includes('CANCELLED') || safe === 'SYSTEM_CANCELLED') {
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    }
    if (safe === 'COMPLETED') {
      return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
    }
    if (safe.includes('WAITING') || safe.includes('REQUIRED')) {
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    }
    return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
  };

  const statusLabel = (status?: string | null) => (status || 'UNKNOWN').replace(/_/g, ' ');

  // Avatar with steam-id tooltip + user-id badge; stops propagation so it never toggles the row
  const renderUserAvatar = (label: string, userId: string, steamId: string, avatar: string | null, profileUrl: string | null) => (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Tooltip arrow title={<div className="p-1 text-[10px] font-bold">{label} · Steam: {steamId}</div>}>
          <a href={profileUrl || '#'} target="_blank" rel="noopener noreferrer" className="relative group">
            <Avatar src={avatar || ''} variant="rounded" className="w-9 h-9 border border-white/5 transition-colors"/>
            <div className="absolute -bottom-1 -right-1 bg-neutral-900 border border-white/10 px-1 rounded text-[8px] font-black text-blue-400">
              {userId}
            </div>
          </a>
        </Tooltip>
      </div>
  );

  // Item thumbnail with rarity accent bar and background fade effect
  const renderItemThumb = (item: ItemDTO, size = 40) => {
    const rarityColor = RARITY_COLORS[item?.details?.rarity as RarityGrade] ?? '#ffffff';
    const gradientWidth = size + 12;
    return (
        <div className="relative flex items-center shrink-0">
          <div style={{width: 3, height: size, background: rarityColor}} className="rounded-full"/>
          <div className="relative ml-1">
            <img src={item?.catalog?.imageLink || ''} referrerPolicy="no-referrer" style={{width: size, height: size}} className="object-contain z-10 relative drop-shadow-md" alt=""/>
            <div className="absolute top-1/2 -left-1 -translate-y-1/2 z-0"
                 style={{width: `${gradientWidth}px`, height: `${size}px`, background: `linear-gradient(to right, ${rarityColor}22, transparent)`}}
            />
          </div>
        </div>
    );
  };

  // The checkbox hit-area: a padded cell that selects (and highlights) the row without expanding it
  const renderCheckCell = (checked: boolean, indeterminate: boolean, onToggle: () => void) => (
      <TableCell padding="none" sx={{border: 'none', borderRadius: 0}}>
        <div
            className="flex items-center justify-center py-2 pl-2 pr-1 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
        >
          <Checkbox
              size="small"
              checked={checked}
              indeterminate={indeterminate}
              sx={{color: '#444', '&.Mui-checked': {color: '#3b82f6'}, '&.MuiCheckbox-indeterminate': {color: '#3b82f6'}}}
          />
        </div>
      </TableCell>
  );

  const chevron = (expanded: boolean) => (
      <KeyboardArrowDown
          fontSize="small"
          className="text-gray-400 transition-transform duration-200"
          style={{transform: expanded ? 'rotate(180deg)' : 'none'}}
      />
  );

  const renderExpanded = (group: TradeSystem[], groupKey: string) => {
    const representative = group[0];
    // Guard: the stepper calls status.endsWith(...) — never hand it an undefined status
    const stepperTrade = {
      ...representative,
      effectiveTradeStatus: representative.effectiveTradeStatus || 'WAITING_FOR_SELLER_CONFIRM'
    } as unknown as Trade;

    const ledgerSearchId = String(representative.tradeGroupId || representative.buyGroupId || representative.sellGroupId || representative.id);

    return (
        <Box className="py-3 px-4 my-2 bg-black/20 rounded-xl border border-white/5 space-y-3">
          <div className="pb-2">
            <TradeStatusStepper trade={stepperTrade}/>
          </div>

          <InlineLedger groupId={ledgerSearchId} money={money}/>

          {group.length > 0 && (
              <>
                <Typography className="!text-sm !font-semibold font-black uppercase text-gray-400 mb-1 mt-2">Items ({group.length})</Typography>
                <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-700">
                  {group.map(sub => (
                      <div key={sub.id} className="flex justify-between items-center p-2 rounded-lg bg-neutral-900/40 border border-white/5">
                        <div className="flex items-center gap-3">
                          {renderItemThumb(sub.item, 32)}
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-gray-200">{sub.item?.catalog?.groupName ?? 'Unknown Item'}</span>
                            <div className="flex gap-1 text-[10px] text-gray-500">
                              <span className="font-mono">TRADE #{sub.id}</span>
                              {sub.item?.catalog?.isStattrak && <span className="text-orange-500 font-bold">ST</span>}
                              {sub.item?.catalog?.wearName && <span>{wearMap[sub.item.catalog.wearName] ?? ''}</span>}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-mono font-bold text-emerald-400">{money(sub.priceMinor)}</span>
                      </div>
                  ))}
                </div>
              </>
          )}

          <InlineTimeline tradeId={representative.id}/>
        </Box>
    );
  };

  // A single rendered row (works for both a group and an individual trade)
  const renderRow = (key: string, group: TradeSystem[]) => {
    const representative = group[0];
    const ids = group.map(t => t.id);
    const isSelected = ids.every(id => selectedIds.includes(id));
    const isPartial = !isSelected && ids.some(id => selectedIds.includes(id));
    const isExpanded = expandedKey === key;

    const mostExpensive = group.reduce((max, t) => (t.priceMinor > max.priceMinor ? t : max), group[0]);
    const extraCount = group.length - 1;
    const totalMinor = group.reduce((sum, t) => sum + t.priceMinor, 0);
    const groupType = representative.tradeGroupLocked && representative.tradeGroupId ? `GROUP #${representative.tradeGroupId}` : 'PENDING PAIR';

    return (
        <React.Fragment key={key}>
          <TableRow
              selected={isSelected}
              onClick={() => toggleExpand(key)}
              className="group bg-gray-50/30 dark:bg-neutral-900/20 transition-all hover:bg-white/[0.03] cursor-pointer"
              sx={{
                '&.Mui-selected': { backgroundColor: 'transparent !important' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04) !important' },
                td: { border: 'none !important' }
              }}
          >
            {renderCheckCell(isSelected, isPartial, () => toggleSelect(ids))}

            <TableCell sx={{border: 'none', borderRadius: 0}}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  {renderItemThumb(mostExpensive.item, 40)}
                  {extraCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[9px] font-black px-1 rounded-full shadow">
                        +{extraCount}
                      </span>
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-gray-200 text-xs leading-tight">{mostExpensive.item?.catalog?.groupName ?? 'Unknown Item'}</span>
                  <span className="text-[9px] text-gray-500 font-mono">{group.length > 1 ? groupType : `TRADE #${representative.id}`}</span>
                </div>
              </div>
            </TableCell>

            <TableCell sx={{border: 'none', borderRadius: 0}}>
              {renderUserAvatar('Seller', representative.sellerUserId, representative.sellerSteamAccountId, representative.sellerSteamAvatarUrl, representative.sellerSteamProfileUrl)}
            </TableCell>
            <TableCell sx={{border: 'none', borderRadius: 0}}>
              {renderUserAvatar('Buyer', representative.buyerUserId, representative.buyerSteamAccountId, representative.buyerSteamAvatarUrl, representative.buyerSteamProfileUrl)}
            </TableCell>

            <TableCell sx={{border: 'none', borderRadius: 0}}>
              <span className={`text-[9px] px-2 py-0.5 font-black uppercase border ${getStatusStyle(representative.effectiveTradeStatus)}`}>
                {statusLabel(representative.effectiveTradeStatus)}
              </span>
            </TableCell>

            <TableCell sx={{border: 'none', borderRadius: 0}}>
              <span className="font-mono font-bold text-emerald-500 text-xs">{money(totalMinor)}</span>
            </TableCell>

            <TableCell sx={{border: 'none', borderRadius: 0}} className="text-[10px] text-gray-500 font-bold">
              {new Date(representative.creationDate).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'})}
            </TableCell>

            <TableCell align="right" sx={{border: 'none', borderRadius: 0}}>
              <div className="flex items-center justify-end gap-1">
                <div onClick={(e) => e.stopPropagation()}>
                  <IconButton
                      size="small"
                      onClick={() => {
                        if (group.length > 1) {
                          setSelectedIds(ids);
                          setActionModal({open: true, mode: 'BATCH', entityType: 'TRADE'});
                        } else {
                          setActionModal({open: true, mode: 'SINGLE', trade: representative, entityId: representative.id, entityType: 'TRADE'});
                        }
                      }}
                  >
                    <Edit sx={{fontSize: 16}} className="text-dark-primary"/>
                  </IconButton>
                </div>
                {chevron(isExpanded)}
              </div>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell style={{paddingBottom: 0, paddingTop: 0, border: 'none'}} colSpan={8}>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {renderExpanded(group, key)}
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
    );
  };

  // Rows: either the client-side groups, or one row per individual trade
  const rows: [string, TradeSystem[]][] = isGrouped
      ? groups
      : trades.map(t => [`trade_${t.id}`, [t]] as [string, TradeSystem[]]);

  return (
      <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh] overflow-hidden">
        <div className="flex flex-row justify-between items-center gap-2 mb-4 shrink-0">
          <div className="flex items-center gap-2">
            <SwapHoriz className="text-dark-primary"/>
            <h2 className="text-sm md:text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">System Trades</h2>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5 mb-4" />

        {/* Filter bar */}
        <div className="flex flex-wrap 2xl:flex-nowrap justify-between items-center gap-3 w-full mb-4 p-3 bg-gray-50/50 dark:bg-white/[0.02] border border-black/5 dark:border-white/5 rounded-xl shadow-inner">
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full 2xl:w-auto grow">
            <div className="flex grow sm:grow-0 min-w-[130px] sm:w-[130px] max-w-[200px] justify-between items-center md:gap-1 gap-2 bg-gray-50 dark:bg-neutral-900/50 md:px-4 px-2 h-11 rounded-xl border border-gray-100 dark:border-white/5 shadow-inner">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Grouped</span>
              <button
                  type="button"
                  onClick={() => { setIsGrouped(prev => !prev); setPage(0); }}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 focus:outline-none ${isGrouped ? 'bg-blue-500 dark:bg-dark-primary' : 'bg-gray-200 dark:bg-neutral-700'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 ${isGrouped ? 'translate-x-4' : 'translate-x-1'}`}/>
              </button>
            </div>

            <FormControl size="small" className="grow sm:grow-0 min-w-[150px] sm:w-[170px] h-11">
              <Select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  MenuProps={{
                    disableScrollLock: true,
                    style: {zIndex: 99999}
                  }}
                  className="w-full h-full bg-gray-50 dark:bg-neutral-800/50 border border-gray-100 dark:border-white/5 text-xs text-gray-700 dark:text-gray-200 shadow-inner font-semibold uppercase tracking-wider"
                  sx={{
                    borderRadius: '0.75rem', overflow: 'hidden', boxShadow: 'none',
                    '.MuiOutlinedInput-notchedOutline': {border: 0},
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {border: 0},
                    '.MuiSelect-select': {display: 'flex', alignItems: 'center', height: '100%', boxSizing: 'border-box', py: 0, pl: '16px', pr: '32px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'}
                  }}
              >
                <MenuItem value="ALL" className="!text-xs !font-medium !uppercase !tracking-wider">ALL STATUSES</MenuItem>
                {['WAITING_FOR_SELLER_CONFIRM', 'WAITING_FOR_SELLER_SEND', 'WAITING_FOR_BUYER_ACCEPT', 'WAITING_FOR_VERIFICATION', 'COMPLETED', 'WAITING_FOR_SELLER_CONFIRM_CANCELLED', 'WAITING_FOR_SELLER_SEND_CANCELLED', 'WAITING_FOR_BUYER_ACCEPT_CANCELLED', 'SYSTEM_CANCELLED'].map(s => (
                    <MenuItem key={s} value={s} className="!text-xs !font-medium !uppercase !tracking-wider">{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ID search: pick which id to search by, then enter its value (ids are shown/accepted as Sqid hashes) */}
            <div className="flex grow sm:grow-0 items-center h-11 rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-neutral-800/50 shadow-inner overflow-hidden">
              <FormControl size="small" className="h-full min-w-[130px] sm:w-[150px]">
                <Select
                    value={idSearchType}
                    onChange={(e) => { setIdSearchType(e.target.value as IdSearchType); setPage(0); }}
                    MenuProps={{
                      disableScrollLock: true,
                      style: {zIndex: 99999}
                    }}
                    className="w-full h-full text-xs text-gray-700 dark:text-gray-200 font-semibold uppercase tracking-wider"
                    sx={{
                      height: '100%', boxShadow: 'none',
                      '.MuiOutlinedInput-notchedOutline': {border: 0},
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {border: 0},
                      '.MuiSelect-select': {display: 'flex', alignItems: 'center', height: '100%', boxSizing: 'border-box', py: 0, pl: '14px', pr: '30px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em'}
                    }}
                >
                  {ID_SEARCH_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value} className="!text-xs !font-medium !uppercase !tracking-wider">{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className="w-px h-6 bg-gray-200 dark:bg-white/10"/>
              <TextField
                  size="small" placeholder="ID…" value={idSearchValue}
                  onChange={(e) => { setIdSearchValue(e.target.value); setPage(0); }}
                  className="grow"
                  sx={{
                    minWidth: 110,
                    '& .MuiInputBase-root': {height: '44px', borderRadius: 0},
                    '& .MuiInputBase-input': {fontSize: '11px', py: 0, px: 2, height: '44px', boxSizing: 'border-box'},
                    '& .MuiOutlinedInput-notchedOutline': {border: 0}
                  }}
              />
            </div>

            {[
              {val: startDate, set: setStartDate},
              {val: endDate, set: setEndDate}
            ].map(({val, set}, idx) => (
                <div key={idx} className="relative grow sm:grow-0 min-w-[130px] sm:w-[150px] h-11">
                  <TextField
                      size="small" type="date" fullWidth value={val}
                      onChange={(e) => { set(e.target.value); setPage(0); }}
                      sx={{
                        height: '100%',
                        '& .MuiInputBase-root': {height: '100%', borderRadius: '0.75rem', bgcolor: 'rgba(0,0,0,0.02)'},
                        '& .MuiInputBase-input': {fontSize: '11px', py: 0, px: 2, height: '100%', boxSizing: 'border-box'},
                        '& .MuiOutlinedInput-notchedOutline': {border: '1px solid rgba(150,150,150,0.2)'}
                      }}
                  />
                </div>
            ))}

            <div className="relative grow sm:grow-0 min-w-[130px] sm:w-[160px] h-11">
              <TextField
                  size="small" placeholder="Item name…" fullWidth value={searchValue}
                  onChange={(e) => { setSearchValue(e.target.value); setPage(0); }}
                  sx={{
                    height: '100%',
                    '& .MuiInputBase-root': {height: '100%', borderRadius: '0.75rem'},
                    '& .MuiInputBase-input': {fontSize: '11px', py: 0, px: 2, height: '100%', boxSizing: 'border-box'},
                    '& .MuiOutlinedInput-notchedOutline': {border: '1px solid rgba(150,150,150,0.2)'}
                  }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {selectedIds.length > 0 && (
                <Button variant="text" size="small" onClick={() => setSelectedIds([])} className="text-[10px] font-black text-gray-500 hover:text-white uppercase mr-2">
                  Clear ({selectedIds.length})
                </Button>
            )}

            <Button
                variant="contained" color="warning"
                onClick={() => setCleanupModalOpen(true)}
                className="h-[36px] px-4 text-[10px] font-black uppercase tracking-widest shadow-none"
                sx={{borderRadius: '0.75rem'}}
                startIcon={<RestartAlt/>}
            >
              Cleanup
            </Button>

            {selectedIds.length > 0 && (
                <Button
                    variant="contained" color="error"
                    onClick={() => setActionModal({open: true, mode: 'BATCH', entityType: 'TRADE'})}
                    className="h-[36px] px-4 text-[10px] font-black uppercase tracking-widest animate-pulse shadow-none"
                    sx={{borderRadius: '0.75rem'}}
                    startIcon={<Gavel/>}
                >
                  Penalty
                </Button>
            )}
          </div>
        </div>

        {loading && trades.length === 0 ? (
            <div className="flex-1 flex justify-center items-center">
              <Lottie animationData={loadingAnimation} loop className="w-64"/>
            </div>
        ) : (
            <TableContainer component={Paper} className="flex-1 bg-transparent shadow-none border-none overflow-y-auto" sx={{borderRadius: 0}}>
              <Table size="small" stickyHeader className="border-separate border-spacing-y-2">
                <TableHead>
                  <TableRow className="bg-gray-50/50 dark:bg-white/[0.02]">
                    <TableCell padding="none" sx={{border: 'none', borderRadius: 0}}>
                      <div className="flex items-center justify-center py-2 pl-2 pr-1">
                        <Checkbox
                            size="small"
                            indeterminate={isSomeSelectedOnPage}
                            checked={isAllSelectedOnPage}
                            onChange={toggleSelectAll}
                            sx={{color: '#444', '&.Mui-checked': {color: '#3b82f6'}}}
                        />
                      </div>
                    </TableCell>
                    {['Item', 'Seller', 'Buyer', 'Status', 'Value', 'Date'].map(h => (
                        <TableCell key={h} sx={{border: 'none', borderRadius: 0}} className="text-[0.8rem] !font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</TableCell>
                    ))}
                    <TableCell align="right" sx={{border: 'none', borderRadius: 0}} className="text-[0.8rem] !font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{border: 'none', borderRadius: 0}} className="text-center py-16 text-gray-500 uppercase font-black text-xs">
                          No trades found.
                        </TableCell>
                      </TableRow>
                  ) : (
                      rows.map(([key, group]) => renderRow(key, group))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        <div className="flex justify-between items-center pt-4 border-t border-white/5 mt-auto shrink-0">
          <TablePagination
              component="div" count={totalCount} page={page} onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{color: '#777', '.MuiTablePagination-selectIcon': {color: '#777'}}}
          />
          <div className="text-[10px] text-gray-500 uppercase font-black">
            Total Trades: {totalCount} | Selected: {selectedIds.length}
          </div>
        </div>

        <TradeActionModal
            open={actionModal.open} mode={actionModal.mode} trade={actionModal.trade}
            entityId={actionModal.entityId} entityType={actionModal.entityType}
            selectedIds={selectedIds} onClose={() => setActionModal({open: false, mode: 'SINGLE'})}
            onSuccess={() => { fetchData(); setSelectedIds([]); }}
        />
        <GlobalTradeCleanupModal
            open={cleanupModalOpen} onClose={() => setCleanupModalOpen(false)} onSuccess={fetchData}
        />
      </div>
  );
};

export default SystemTrades;